import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    listOffboarding, updateOffboarding, updateOffboardingTask,
    applyOffboarding, rehireWorker, listCobraEvents, updateCobraEvent,
} from 'services/offboarding';
import { toast } from 'components/Toast';

const fmt = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return iso; }
};

const statusColor = (s) => {
    const v = (s || '').toLowerCase();
    if (v === 'scheduled') return '#b45309';
    if (v === 'applied') return '#15803d';
    if (v === 'cancelled') return '#64748b';
    return '#334155';
};

const OffboardingPage = () => {
    const qc = useQueryClient();
    const [tab, setTab] = useState('active'); // active | cobra
    const { data = [], isLoading } = useQuery({
        queryKey: ['offboarding'],
        queryFn: () => listOffboarding(),
        refetchInterval: 60000,
    });
    const { data: cobra = [] } = useQuery({
        queryKey: ['cobra'],
        queryFn: () => listCobraEvents(),
        enabled: tab === 'cobra',
    });

    const rows = useMemo(() => {
        const list = Array.isArray(data) ? data : (data?.items || []);
        return list.map(r => ({
            id: r.id || r.Id,
            status: r.status || r.Status,
            type: r.offboardType || r.OffboardType || r.workerType || r.WorkerType,
            workerId: r.workerId || r.WorkerId,
            effectiveDate: r.effectiveDate || r.EffectiveDate,
            reason: r.reason || r.Reason,
            finalPayPath: r.finalPayPath || r.FinalPayPath,
            tasks: r.tasks || r.Tasks || [],
            notes: r.notes || r.Notes,
        }));
    }, [data]);

    const active = rows.filter(r => ['scheduled', 'applied'].includes((r.status || '').toLowerCase())
        && ['employee', 'contractor'].includes((r.type || '').toLowerCase()));
    const openChecklist = active.filter(r => {
        const tasks = r.tasks || [];
        if ((r.status || '').toLowerCase() === 'scheduled') return true;
        return tasks.some(t => !(t.isDone ?? t.IsDone));
    });

    const mutTask = useMutation({
        mutationFn: ({ id, taskId, isDone }) => updateOffboardingTask(id, taskId, isDone),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['offboarding'] }),
        onError: (e) => toast(e?.response?.data?.message || 'Update failed', 'error'),
    });
    const mutCancel = useMutation({
        mutationFn: (id) => updateOffboarding(id, { cancel: true }),
        onSuccess: () => { toast('Scheduled offboarding cancelled'); qc.invalidateQueries({ queryKey: ['offboarding'] }); },
        onError: (e) => toast(e?.response?.data?.message || 'Cancel failed', 'error'),
    });
    const mutApply = useMutation({
        mutationFn: (id) => applyOffboarding(id),
        onSuccess: () => { toast('Offboarding applied'); qc.invalidateQueries({ queryKey: ['offboarding'] }); },
        onError: (e) => toast(e?.response?.data?.message || 'Apply failed', 'error'),
    });
    const mutRehire = useMutation({
        mutationFn: (id) => rehireWorker(id),
        onSuccess: () => { toast('Worker rehired'); qc.invalidateQueries({ queryKey: ['offboarding'] }); qc.invalidateQueries({ queryKey: ['employees'] }); },
        onError: (e) => toast(e?.response?.data?.message || 'Rehire failed', 'error'),
    });

    return (
        <div style={{ padding: '8px 4px', maxWidth: 960 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Offboarding</h2>
                    <p style={{ margin: '6px 0 0', fontSize: 13, color: '#64748b' }}>
                        Scheduled terminations and checklist progress. {openChecklist.length} open.
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    {['active', 'cobra'].map(t => (
                        <button key={t} type="button" onClick={() => setTab(t)}
                            style={{
                                border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 14px', cursor: 'pointer',
                                background: tab === t ? '#1B75F0' : '#fff', color: tab === t ? '#fff' : '#334155', fontWeight: 600, fontSize: 13,
                            }}>
                            {t === 'active' ? 'Checklist' : 'COBRA'}
                        </button>
                    ))}
                </div>
            </div>

            {tab === 'active' && (
                isLoading ? <div style={{ color: '#94a3b8' }}>Loading…</div>
                : active.length === 0 ? (
                    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 32, textAlign: 'center', color: '#94a3b8' }}>
                        No active worker offboarding. Use Terminate on an employee or contractor to start.
                    </div>
                ) : active.map(r => {
                    const tasks = r.tasks || [];
                    const doneCount = tasks.filter(t => t.isDone ?? t.IsDone).length;
                    return (
                        <div key={r.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, marginBottom: 16 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: 15 }}>
                                        {(r.type || 'worker').toUpperCase()} · {r.workerId}
                                    </div>
                                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                                        Effective {fmt(r.effectiveDate)} · Final pay: {r.finalPayPath || '—'} · Reason: {r.reason || '—'}
                                    </div>
                                </div>
                                <span style={{
                                    alignSelf: 'flex-start', fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
                                    color: statusColor(r.status), background: '#f8fafc', border: '1px solid #e2e8f0',
                                    borderRadius: 20, padding: '4px 10px',
                                }}>{r.status}</span>
                            </div>

                            <div style={{ marginTop: 14 }}>
                                <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 8 }}>
                                    Checklist ({doneCount}/{tasks.length})
                                </div>
                                {tasks.sort((a, b) => (a.sortOrder ?? a.SortOrder ?? 0) - (b.sortOrder ?? b.SortOrder ?? 0)).map(t => {
                                    const tid = t.id || t.Id;
                                    const done = t.isDone ?? t.IsDone;
                                    return (
                                        <label key={tid} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '6px 0', fontSize: 14, color: '#334155' }}>
                                            <input
                                                type="checkbox"
                                                checked={!!done}
                                                onChange={e => mutTask.mutate({ id: r.id, taskId: tid, isDone: e.target.checked })}
                                            />
                                            <span style={{ textDecoration: done ? 'line-through' : 'none', color: done ? '#94a3b8' : undefined }}>
                                                {t.label || t.Label}
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>

                            <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                                {(r.status || '').toLowerCase() === 'scheduled' && (
                                    <>
                                        <button type="button" onClick={() => mutApply.mutate(r.id)}
                                            style={{ border: '1px solid #1B75F0', color: '#1B75F0', background: '#fff', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                            Apply now
                                        </button>
                                        <button type="button" onClick={() => mutCancel.mutate(r.id)}
                                            style={{ border: '1px solid #e2e8f0', background: '#fff', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                            Cancel schedule
                                        </button>
                                    </>
                                )}
                                {(r.status || '').toLowerCase() === 'applied' && (
                                    <button type="button" onClick={() => {
                                        if (window.confirm('Rehire this worker? Portal can be reactivated.')) mutRehire.mutate(r.id);
                                    }}
                                        style={{ border: '1px solid #16a34a', color: '#16a34a', background: '#fff', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                        Rehire
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })
            )}

            {tab === 'cobra' && (
                <div>
                    {(Array.isArray(cobra) ? cobra : []).length === 0 ? (
                        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 24, color: '#94a3b8' }}>
                            No COBRA events yet. Events are created when employees with health benefits are offboarded.
                        </div>
                    ) : (Array.isArray(cobra) ? cobra : []).map(ev => {
                        const id = ev.id || ev.Id;
                        return (
                            <div key={id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 12 }}>
                                <div style={{ fontWeight: 700 }}>Employee {ev.employeeId || ev.EmployeeId}</div>
                                <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                                    Event {fmt(ev.qualifyingEventDate || ev.QualifyingEventDate)} · Status {ev.electionStatus || ev.ElectionStatus}
                                    · Deadline {fmt(ev.electionDeadline || ev.ElectionDeadline)}
                                </div>
                                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                                    {['notified', 'elected', 'declined', 'expired'].map(st => (
                                        <button key={st} type="button"
                                            onClick={() => updateCobraEvent(id, {
                                                electionStatus: st,
                                                noticeSentAt: st === 'notified' ? new Date().toISOString() : undefined,
                                            }).then(() => { toast('COBRA updated'); qc.invalidateQueries({ queryKey: ['cobra'] }); })}
                                            style={{ fontSize: 11, border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', background: '#fff' }}>
                                            Mark {st}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default OffboardingPage;
